import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Flex, freddyTheme, Link, TextAccented } from '@coolblue-development/freddy';
import selectUspTrigger from '@coolblue-development/ga4-events/triggers/ContentBlocks/Usps/selectUspTrigger';
import viewDeliveryPropositionUspTrigger from '@coolblue-development/ga4-events/triggers/ContentBlocks/Usps/viewDeliveryPropositionUspTrigger';
import { Box as BoxIcon, Checkmark, FacebookCircled, FlamingSled, Leaf, Present, SmileHappy, Snowflake, Star, YouTubeCircled, } from '@coolblue-development/icons';
import { TrackWhenVisible, useAnalytics } from '@coolblue-development/next-analytics/client';
import { EcoCheques, Trustpilot } from '@coolblue-development/next-patterns/client';
import { useTranslation } from '../../../i18n/useTranslation.js';
import { IconType } from '../../../types.js';
const DELIVERY_USP_TYPE = 'delivery';
export const Proposition = ({ proposition, ga4Params, index, changePhoneNumberButtonUrl, }) => {
    const { t: translations } = useTranslation('proposition');
    let iconElement;
    if (typeof proposition.icon === 'object') {
        iconElement = proposition.icon;
    }
    if (!iconElement) {
        switch (proposition.icon) {
            case IconType.Checkmark:
                iconElement = _jsx(Checkmark, { fill: freddyTheme.color.icon.sentimentPositive });
                break;
            case IconType.Youtube:
                iconElement = _jsx(YouTubeCircled, { fill: freddyTheme.color.icon.base });
                break;
            case IconType.Box:
                iconElement = _jsx(BoxIcon, { fill: freddyTheme.color.icon.base });
                break;
            case IconType.Star:
                iconElement = _jsx(Star, { fill: freddyTheme.color.icon.base });
                break;
            case IconType.CircleFacebook:
                iconElement = _jsx(FacebookCircled, { fill: freddyTheme.color.icon.base });
                break;
            case IconType.CircleYoutube:
                iconElement = _jsx(YouTubeCircled, { fill: freddyTheme.color.icon.base });
                break;
            case IconType.SmileHappy:
                iconElement = _jsx(SmileHappy, { fill: freddyTheme.color.icon.base });
                break;
            case IconType.TrustPilot:
                iconElement = _jsx(Trustpilot, { width: 8 });
                break;
            case IconType.Snowflake:
                iconElement = _jsx(Snowflake, { fill: freddyTheme.color.icon.base });
                break;
            case IconType.FlamingSled:
                iconElement = _jsx(FlamingSled, { fill: freddyTheme.color.icon.base });
                break;
            case IconType.Present:
                iconElement = _jsx(Present, { fill: freddyTheme.color.icon.base });
                break;
            case IconType.Leaf:
                iconElement = _jsx(Leaf, { fill: freddyTheme.color.icon.sentimentPositive });
                break;
            default:
                iconElement = _jsx(Checkmark, { fill: freddyTheme.color.icon.sentimentPositive });
                break;
        }
    }
    const isDeliveryUsp = proposition.type === DELIVERY_USP_TYPE;
    const regex = /coolblue\.[a-z]{2,3}/;
    const url = proposition.link ? new URL(proposition.link) : '';
    const isCoolblueUrl = url ? regex.test(url.hostname) : false;
    return (_jsxs(_Fragment, { children: [isDeliveryUsp && (_jsx(TrackWhenVisible, { trigger: viewDeliveryPropositionUspTrigger({
                    content: extractAndCheckCutOffTime(proposition.text),
                    index: index,
                    total_indices: 1,
                    correlation_id: ga4Params.correlation_id,
                }), component: "span", children: _jsx(PropositionComponent, { proposition: proposition, ga4Params: ga4Params, index: index, iconElement: iconElement, isCoolblueUrl: isCoolblueUrl, changePhoneNumberButtonUrl: changePhoneNumberButtonUrl, translations: translations }) })), !isDeliveryUsp && (_jsx(PropositionComponent, { proposition: proposition, ga4Params: ga4Params, index: index, iconElement: iconElement, isCoolblueUrl: isCoolblueUrl, changePhoneNumberButtonUrl: changePhoneNumberButtonUrl, translations: translations }))] }));
};
const PropositionComponent = ({ proposition, ga4Params, index, iconElement, isCoolblueUrl, translations, changePhoneNumberButtonUrl, }) => {
    const { trackEvent } = useAnalytics();
    return (_jsxs(_Fragment, { children: [proposition.link && (_jsx(Link, { onClick: () => {
                    trackEvent(selectUspTrigger({
                        sub_feature: ga4Params.sub_feature,
                        index: index,
                        total_indices: 1,
                        url: proposition.link,
                        redirect: proposition.text,
                        correlation_id: ga4Params.correlation_id,
                    }));
                }, sx: proposition.icon === IconType.FlamingSled
                    ? {
                        marginTop: '-0.6rem',
                        alignItems: 'center',
                        svg: {
                            width: '3.2rem !important',
                            height: '3.2rem !important',
                        },
                    }
                    : {}, variant: "usp", startIcon: iconElement, href: proposition.link, target: isCoolblueUrl ? '_self' : '_blank', children: _jsx(Box, { sx: { whiteSpace: 'wrap' }, children: parseText(proposition.text, translations, changePhoneNumberButtonUrl) }) })), !proposition.link && (_jsxs(Flex, { mb: "0.8rem", children: [_jsx(Box, { sx: {
                            width: '1.6rem',
                            height: '1.6rem',
                            marginTop: '-0.2rem',
                        }, children: iconElement }), _jsx(Box, { ml: 200, sx: { whiteSpace: proposition.type === 'temporary-out-of-stock' ? '' : 'wrap' }, children: parseText(proposition.text, translations, changePhoneNumberButtonUrl) })] }))] }));
};
const parseText = (text, translations, changePhoneNumberButtonUrl) => {
    const regex = /{accented}(.*?){\/accented}|{ecoCheques}|{changePhoneNumberButton}/g;
    const parts = [];
    let match;
    let lastIndex = 0;
    while ((match = regex.exec(text)) !== null) {
        if (match.index > lastIndex) {
            parts.push(text.substring(lastIndex, match.index));
        }
        if (match[0] === '{ecoCheques}') {
            parts.push(_jsx(EcoCheques, { style: { fontSize: 'inherit', width: 'auto', height: 'auto', marginTop: 0 } }, `eco-${match.index}`));
        }
        else if (match[0] === '{changePhoneNumberButton}') {
            parts.push(_jsx(Link, { href: changePhoneNumberButtonUrl, children: translations('change') }, `change-${match.index}`));
        }
        else if (match[1]) {
            parts.push(_jsx(TextAccented, { children: match[1] }, `acc-${match.index}`));
        }
        lastIndex = match.index + match[0].length;
    }
    if (lastIndex < text.length) {
        parts.push(text.substring(lastIndex));
    }
    return parts;
};
export const extractAndCheckCutOffTime = (text) => {
    const timePattern = /\b([01]?[0-9]|2[0-3]):[0-5][0-9]\b/;
    const match = text.match(timePattern);
    if (!match) {
        return 'other';
    }
    if (match[0] === '13:00') {
        return '1300';
    }
    if (match[0] === '15:00') {
        return '1500';
    }
    if (match[0] === '17:00') {
        return '1700';
    }
    if (match[0] === '23:59') {
        return '2359';
    }
    return 'other';
};
//# sourceMappingURL=Proposition.js.map